import AlreadySignedUpDialog from "@components/AlreadySignedUpDialog"
import Button from "@components/Button"
import InfoBubble from "@components/InfoBubble"
import Input from "@components/Input"
import Layout from "@components/Layout"
import PageHeading from "@components/PageHeading"
import { trackCompleteRegistrationEvent } from "@helpers/facebookTracking"
import {
  getPasswordFromSessionStorage,
  getUserId,
  removePasswordFromSessionStorage,
} from "@helpers/sessionStorage"
import { signUp } from "@helpers/supabase"
import { validateEmail } from "@helpers/validations"
import { IsUserSignedInLoaderData } from "@loaders/isUserSignedInLoader"
import { setUserId } from "@snowplow/browser-tracker"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Form,
  Navigate,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom"

export default function EmailPage() {
  const loaderData = useLoaderData() as IsUserSignedInLoaderData | null
  const location = useLocation()

  // Determine the base path from current location (handles both /onboarding and /onboarding/v1)
  const basePath = location.pathname.replace(/\/email$/, "")

  // Read password from session storage (set on previous page)
  const password = getPasswordFromSessionStorage()

  // If user is already signed in, skip to preparing
  if (loaderData?.isUserSignedIn) {
    return <Navigate to="/sign-up/preparing" />
  }

  // If no password is stored, go back to password step
  if (!password) {
    return <Navigate to={`${basePath}/password`} />
  }

  return <EmailForm password={password} />
}

function EmailForm({ password }: { password: string }) {
  const [email, setEmail] = useState("")
  const [emailValid, setEmailValid] = useState(true)
  const [openAlreadySignedUpDialog, setOpenAlreadySignedUpDialog] =
    useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation()
  const isEmailValid = validateEmail(email)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return

    if (isEmailValid) {
      setIsSubmitting(true)
      const { error } = await signUp({ email, password })

      if (error) {
        if (error.message === "User already registered") {
          setOpenAlreadySignedUpDialog(true)
        } else {
          console.error(error)
        }
        setIsSubmitting(false)
        return
      }

      removePasswordFromSessionStorage()

      const userId = await getUserId()

      if (userId) {
        setUserId(userId)
      }

      await trackCompleteRegistrationEvent()

      setIsSubmitting(false)
      // Continue to preparing screen
      navigate("/sign-up/preparing")
    } else {
      setEmailValid(false)
    }
  }

  return (
    <>
      <Layout>
        <PageHeading>{t("pages.signUp.email.heading")}</PageHeading>

        {/* Info bubble */}
        <InfoBubble variant="yellow">
          {t(
            emailValid
              ? "pages.signUp.email.subheading"
              : "pages.signUp.email.emailNotValid",
          )}
        </InfoBubble>

        {/* Form */}
        <Form noValidate onSubmit={handleSubmit} className="custom-form px-4">
          <Input
            autoFocus
            invalid={!emailValid}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={t("shared.email")}
            type="email"
            value={email}
          />

          <div className="mt-auto pt-6">
            <Button
              disabled={email.length < 1 || isSubmitting}
              type="submit"
              title={t("shared.continue")}
            />
          </div>
        </Form>
      </Layout>

      {/* Dialog for "User already registered" scenario */}
      <AlreadySignedUpDialog
        open={openAlreadySignedUpDialog}
        onClose={() => setOpenAlreadySignedUpDialog(false)}
      />
    </>
  )
}
